import React from 'react';

import { studioTheme, STUDIO_SERIF, PINBOARD_PAPER, readableInk, hexToRgba } from './studioTheme';
import { StudioWrap, SourceLogo, Stars, DateText, secondaryLine } from './StudioCardKit';
import { StudioBody, isMediaTestimonial, StudioMediaCard } from './StudioBody';
import { socialCardKeys } from '@/src/constants/socialcard.constant';

/**
 * Pinboard (widget type 15)
 *
 * A note on yellow paper with a strip of tape and a slight tilt, set in an
 * italic serif. It ignores dark mode the way a pinned note would, but it does
 * follow the wall's Card Color, which selecting this type resets to yellow.
 */
const PinboardCard = (props) => {
  const { cardItem, socialCardType } = props;
  const t = studioTheme(props);

  /* The note takes the wall's Card Color. Selecting this widget type resets
     that to PINBOARD_PAPER (see the widget picker) so it starts as a yellow
     note, but the customer can repaint it and the whole note follows. The ink
     is derived, not fixed, so text stays readable on a dark note. */
  const paper = (t.cardBackgroundObject && t.cardBackgroundObject.backgroundColor) || PINBOARD_PAPER;
  const paperInk = readableInk(paper, '#2a2410', '#fdfdf5');
  const paperMuted = hexToRgba(paperInk, 0.7);

  const name = cardItem && cardItem[socialCardKeys.authorName];
  const role = secondaryLine(cardItem);
  const link = cardItem && cardItem[socialCardKeys.messageLink];

  /* One body type scale per card: the text testimonial and the video/audio
     summary must read as the same widget. */
  const bodyTextStyle = { fontFamily: STUDIO_SERIF, fontStyle: 'italic', fontSize: t.smallSize ? 17 : 19, lineHeight: 1.35, color: paperInk };

  /* A video or audio testimonial IS the card - the player already carries
     the name and work title, so none of the chrome below is drawn. Only the
     summary keeps this widget's look, as the panel it extends into. */
  if (isMediaTestimonial(cardItem, socialCardType)) {
    return (
      <StudioMediaCard
        cardItem={cardItem}
        socialCardType={socialCardType}
        t={t}
        enclosure={{
          /* a pinned note is tilted, video or not, so wrap the player even with no summary. */
          always: true,
          borderRadius: 4,
          background: paper,
          color: paperInk,
          boxShadow: '0 12px 26px rgba(80,60,0,0.18)',
          transform: 'rotate(-1.2deg)',
        }}
        summaryStyle={bodyTextStyle}
      />
    );
  }

  return (
    <StudioWrap
      style={{ paddingTop: 16, paddingBottom: 16 }}
    >
      <div
        style={{
          position: 'relative',
          borderRadius: 4,
          padding: '28px 22px 20px',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
          background: paper,
          color: paperInk,
          boxShadow: '0 12px 26px rgba(80,60,0,0.18)',
          transform: 'rotate(-1.2deg)',
        }}
      >
        {/* tape */}
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: '50%',
            top: -8,
            width: 90,
            height: 22,
            transform: 'translateX(-50%) rotate(2deg)',
            background: 'rgba(255,255,255,0.55)',
            border: '1px solid rgba(0,0,0,0.05)',
          }}
        />

        <StudioBody
          t={t}
          cardItem={cardItem}
          socialCardType={socialCardType}
          mediaRadius={2}
          textStyle={bodyTextStyle}
        />

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <Stars t={t} cardItem={cardItem} size={13} color="#b5851a" />
          <SourceLogo cardItem={cardItem} />
        </div>

        <div
          className={t.fontClass}
          style={{ fontFamily: t.fontStyle ? undefined : STUDIO_SERIF, fontSize: 15, color: paperMuted }}
        >
          {'— '}
          <a
            href={link || undefined}
            target={link ? '_blank' : '_self'}
            rel="noreferrer noopener"
            style={{ color: paperInk, textDecoration: 'none' }}
          >
            {name}
          </a>
          {role ? `, ${role}` : null}
        </div>

        <DateText t={t} cardItem={cardItem} color={paperMuted} />
      </div>
    </StudioWrap>
  );
};

export default PinboardCard;
